import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { environment } from '@env';

import {
  AddItTicketAreasPayload,
  AddItTicketSystemPayload,
  AssignItTicketPayload,
  ChangeItTicketStatusPayload,
  ClassifyItTicketPayload,
  ItTicketAiDecision,
  CreateItTicketCommentPayload,
  CreateItTicketLinkPayload,
  CreateItTicketPayload,
  GetItTicketsParams,
  InitItTicketAttachmentPayload,
  InitItTicketAttachmentResponse,
  ItTicket,
  ItTicketArea,
  ItTicketAreaCatalogItem,
  ItTicketAttachment,
  ItTicketCatalogs,
  ItTicketComment,
  ItTicketDetail,
  ItTicketLink,
  ItTicketPeople,
  ItTicketSearchResult,
  ItTicketSlaPolicy,
  ItTicketSystem,
  ItTicketsListResponse,
  UpdateItTicketPayload,
  UpdateItTicketSlaPolicyPayload,
} from '../../../interfaces/it-ticket.interface';
import { ItTicketsApiService } from './it-tickets-api.service';

/**
 * Cliente de la API de tickets de T.I. (`/it-support`).
 *
 * Sigue el contrato de la fase 2: filtros estilo Spatie, fechas ISO en UTC y
 * `camelCase` aunque la base sea `snake_case`.
 */
@Injectable()
export class ItTicketsHttpService extends ItTicketsApiService {
  private readonly http = inject(HttpClient);
  // La mesa vive en su propia API Gateway (`TerraEnergyItSupport-Api-{env}`) con
  // los mismos paths: la compartida llegó al tope de 300 recursos.
  private readonly baseUrl = `${environment.itSupportApiGateway}it-support`;
  private readonly ticketsUrl = `${this.baseUrl}/tickets`;

  getCatalogs(): Observable<ItTicketCatalogs> {
    return this.http.get<ItTicketCatalogs>(`${this.baseUrl}/catalogs`);
  }

  getPeople(): Observable<ItTicketPeople> {
    return this.http.get<ItTicketPeople>(`${this.baseUrl}/people`);
  }

  getTickets(params: GetItTicketsParams): Observable<ItTicketsListResponse> {
    return this.http.get<ItTicketsListResponse>(this.ticketsUrl, {
      params: this.buildListParams(params),
    });
  }

  searchTickets(query: string): Observable<ItTicketSearchResult[]> {
    return this.http
      .get<{ tickets: ItTicketSearchResult[] }>(`${this.ticketsUrl}/search`, {
        params: new HttpParams().set('q', query),
      })
      .pipe(map((response) => response.tickets));
  }

  getTicket(ticketId: number): Observable<ItTicketDetail> {
    return this.http.get<ItTicketDetail>(`${this.ticketsUrl}/${ticketId}`);
  }

  createTicket(payload: CreateItTicketPayload): Observable<ItTicketDetail> {
    return this.http.post<ItTicketDetail>(this.ticketsUrl, payload);
  }

  updateTicket(ticketId: number, payload: UpdateItTicketPayload): Observable<ItTicket> {
    return this.http.put<ItTicket>(`${this.ticketsUrl}/${ticketId}`, payload);
  }

  appendDescription(ticketId: number, body: string): Observable<ItTicketDetail> {
    return this.http.post<ItTicketDetail>(`${this.ticketsUrl}/${ticketId}/description`, { body });
  }

  /** El motivo sólo viaja si trae texto: un `reason` vacío no dice nada */
  assignTicket(ticketId: number, payload: AssignItTicketPayload): Observable<ItTicket> {
    const reason = payload.reason?.trim();
    return this.http.post<ItTicket>(`${this.ticketsUrl}/${ticketId}/assign`, {
      assignedUserId: payload.assignedUserId,
      ...(reason ? { reason } : {}),
    });
  }

  classifyTicket(ticketId: number, payload: ClassifyItTicketPayload): Observable<ItTicket> {
    const reason = payload.reason?.trim();
    return this.http.post<ItTicket>(`${this.ticketsUrl}/${ticketId}/classification`, {
      urgencyId: payload.urgencyId,
      impactId: payload.impactId,
      ...(reason ? { reason } : {}),
    });
  }

  decideAiProposal(
    ticketId: number,
    classificationId: number,
    decision: ItTicketAiDecision,
  ): Observable<ItTicket> {
    return this.http.post<ItTicket>(
      `${this.ticketsUrl}/${ticketId}/classifications/${classificationId}/decision`,
      { decision },
    );
  }

  updateAreaWeight(areaId: number, weight: number): Observable<ItTicketAreaCatalogItem> {
    return this.http.put<ItTicketAreaCatalogItem>(`${this.baseUrl}/area-weights/${areaId}`, {
      weight,
    });
  }

  updateSlaPolicy(
    priorityId: number,
    payload: UpdateItTicketSlaPolicyPayload,
  ): Observable<ItTicketSlaPolicy> {
    return this.http.put<ItTicketSlaPolicy>(`${this.baseUrl}/sla-policies/${priorityId}`, payload);
  }

  changeStatus(ticketId: number, payload: ChangeItTicketStatusPayload): Observable<ItTicket> {
    return this.http.post<ItTicket>(`${this.ticketsUrl}/${ticketId}/status`, payload);
  }

  addAreas(ticketId: number, payload: AddItTicketAreasPayload): Observable<ItTicketArea[]> {
    return this.http
      .post<{ areas: ItTicketArea[] }>(`${this.ticketsUrl}/${ticketId}/areas`, payload)
      .pipe(map((response) => response.areas));
  }

  addComment(ticketId: number, payload: CreateItTicketCommentPayload): Observable<ItTicketComment> {
    return this.http.post<ItTicketComment>(`${this.ticketsUrl}/${ticketId}/comments`, payload);
  }

  addLink(ticketId: number, payload: CreateItTicketLinkPayload): Observable<ItTicketLink> {
    return this.http.post<ItTicketLink>(`${this.ticketsUrl}/${ticketId}/links`, payload);
  }

  removeLink(ticketId: number, linkId: number): Observable<ItTicketLink> {
    return this.http.delete<ItTicketLink>(`${this.ticketsUrl}/${ticketId}/links/${linkId}`);
  }

  addSystem(ticketId: number, payload: AddItTicketSystemPayload): Observable<ItTicketSystem> {
    return this.http.post<ItTicketSystem>(`${this.ticketsUrl}/${ticketId}/systems`, payload);
  }

  removeSystem(ticketId: number, systemId: number): Observable<ItTicketSystem> {
    return this.http.delete<ItTicketSystem>(`${this.ticketsUrl}/${ticketId}/systems/${systemId}`);
  }

  initAttachment(
    ticketId: number,
    payload: InitItTicketAttachmentPayload,
  ): Observable<InitItTicketAttachmentResponse> {
    return this.http.post<InitItTicketAttachmentResponse>(
      `${this.ticketsUrl}/${ticketId}/attachments`,
      payload,
    );
  }

  /**
   * El PUT va directo a S3, no a la API. El `Content-Type` tiene que ser idéntico
   * al declarado al reservar porque la firma lo incluye; mandar otro —o dejar que
   * el navegador lo adivine— hace que S3 responda 403. La URL vive 15 minutos.
   */
  uploadToPresignedUrl(uploadUrl: string, file: File): Observable<void> {
    return this.http.put<void>(uploadUrl, file, {
      headers: { 'Content-Type': file.type },
    });
  }

  confirmAttachment(ticketId: number, attachmentId: number): Observable<ItTicketAttachment> {
    return this.http.post<ItTicketAttachment>(
      `${this.ticketsUrl}/${ticketId}/attachments/${attachmentId}/confirm`,
      {},
    );
  }

  removeAttachment(ticketId: number, attachmentId: number): Observable<ItTicketAttachment> {
    return this.http.delete<ItTicketAttachment>(
      `${this.ticketsUrl}/${ticketId}/attachments/${attachmentId}`,
    );
  }

  /**
   * Traduce los filtros de la pantalla a la convención de la plataforma:
   * `filter[campo]` estilo Spatie, `sort=-campo` y `page`. Los filtros vacíos no
   * viajan, para que la URL refleje sólo lo que el usuario realmente eligió.
   */
  private buildListParams(params: GetItTicketsParams): HttpParams {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 1))
      .set('perPage', String(params.perPage ?? 25));

    if (params.statusIds?.length) {
      httpParams = httpParams.set('filter[statusId]', params.statusIds.join(','));
    }
    if (params.isOpen !== undefined) {
      httpParams = httpParams.set('filter[isOpen]', String(params.isOpen));
    }
    if (params.typeIds?.length) {
      httpParams = httpParams.set('filter[typeId]', params.typeIds.join(','));
    }
    if (params.platformIds?.length) {
      httpParams = httpParams.set('filter[platformId]', params.platformIds.join(','));
    }
    if (params.priorityIds?.length) {
      httpParams = httpParams.set('filter[priorityId]', params.priorityIds.join(','));
    }
    if (params.areaIds?.length) {
      httpParams = httpParams.set('filter[areaId]', params.areaIds.join(','));
    }
    // `null` explícito es un filtro válido y distinto de "sin filtro": es la cola
    // de triaje, los tickets que no tienen dueño.
    if (params.assignedUserId === null) {
      httpParams = httpParams.set('filter[assignedUserId]', 'null');
    } else if (params.assignedUserId !== undefined) {
      httpParams = httpParams.set('filter[assignedUserId]', String(params.assignedUserId));
    }
    if (params.requesterUserId) {
      httpParams = httpParams.set('filter[requesterUserId]', String(params.requesterUserId));
    }
    if (params.createdFrom) {
      httpParams = httpParams.set('filter[createdFrom]', params.createdFrom);
    }
    if (params.createdTo) {
      httpParams = httpParams.set('filter[createdTo]', params.createdTo);
    }
    if (params.search) {
      httpParams = httpParams.set('filter[search]', params.search);
    }
    if (params.systemId) {
      httpParams = httpParams.set('filter[systemId]', String(params.systemId));
    }
    if (params.sort) {
      httpParams = httpParams.set('sort', params.sort);
    }

    return httpParams;
  }
}
